import { c as _c } from "react/compiler-runtime";
import type { ReactNode, ComponentPropsWithoutRef } from "react";
import { motion } from "motion/react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/cn";

// No `backdrop-blur` here on purpose. A backdrop-filter per bubble meant one
// composited layer — and one GPU texture — per message, growing with an uncapped
// history, inside a panel that is already blurred. Visually it cost nothing:
// user bubbles are opaque (`bg-purple`), so their blur never showed, and
// `bg-white/80` over the panel's own blur is all but indistinguishable. The blur
// stays on the fixed-count chrome (header, footer, system pill), where it shows.
const speechBubbleVariants = cva("px-3 py-3 rounded-3xl outline-1 outline-white/20", {
  variants: {
    sender: {
      assistant: "bg-white/80 rounded-bl-xs text-black",
      user: "bg-purple rounded-br-xs text-white"
    }
  },
  defaultVariants: {
    sender: "assistant"
  }
});
type MotionDivProps = Omit<ComponentPropsWithoutRef<typeof motion.div>, "children" | "className">;
interface SpeechBubbleProps extends VariantProps<typeof speechBubbleVariants>, MotionDivProps {
  children?: ReactNode;
  className?: string;
}
export const SpeechBubble: React.FC<SpeechBubbleProps> = t0 => {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let sender;
  if ($[0] !== t0) {
    ({
      sender,
      children,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = sender;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    sender = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== sender) {
    t1 = cn(speechBubbleVariants({
      sender
    }), className);
    $[5] = className;
    $[6] = sender;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  let t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0,
      y: 50
    };
    t3 = {
      opacity: 1,
      y: 0
    };
    t4 = {
      duration: 0.3,
      ease: "easeOut"
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
  }
  let t5;
  if ($[11] !== children || $[12] !== props || $[13] !== t1) {
    t5 = <motion.div className={t1} initial={t2} animate={t3} transition={t4} {...props}>{children}</motion.div>;
    $[11] = children;
    $[12] = props;
    $[13] = t1;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
};